import { OptgroupHTMLAttributes, forwardRef, useMemo } from 'react';
import { cx } from 'class-variance-authority';
import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import { pipe } from 'fp-ts/lib/function';
import * as styles from './styles';
import { Label } from '../Label';
import { InputError } from '../InputError';
import { hasError } from '../utils/error';
import { Typography } from '../Typography';
import * as a from 'fp-ts/lib/Array';
import * as o from 'fp-ts/lib/Option';

export type GroupedOption = {
  group?: OptgroupHTMLAttributes<string>;
  children: Option[];
};

type Option = {
  key?: string;
  label: string;
  value: string;
  disabled?: boolean;
};

type SelectCommon = {
  label: string;
  placeholder?: string;
  placeholderDisabled?: boolean;
  name: string;
  error?: string | string[];
  helpText?: string;
};

type SelectConditional =
  | {
      options?: never;
      groupedOptions: GroupedOption[];
    }
  | {
      options: Option[];
      groupedOptions?: never;
    };

type SelectTypeMap = BaseTypeMap<'select', SelectCommon & SelectConditional>;

export type SelectProps = ComponentProps<SelectTypeMap>;

const generateOptions = (options: Option[]) =>
  pipe(
    options,
    a.map(({ key, label, value, disabled }) => (
      <Box as="option" key={key || value} value={value} disabled={disabled}>
        {label}
      </Box>
    ))
  );

const generateGroupedOptions = (groupedOptions: GroupedOption[]) =>
  pipe(
    groupedOptions,
    a.map(({ group, children }) =>
      pipe(
        group,
        o.fromPredicate((x): x is OptgroupHTMLAttributes<string> => !!x),
        o.map((optGroup) => (
          <Box
            as="optgroup"
            key={optGroup.label}
            label={optGroup.label}
            disabled={optGroup.disabled}
          >
            {generateOptions(children)}
          </Box>
        )),
        o.getOrElse(() => <>{generateOptions(children)}</>)
      )
    )
  );

export const Select: Component<SelectTypeMap> = forwardRef(function Select(
  {
    options,
    groupedOptions,
    className,
    name,
    label,
    placeholder,
    error,
    helpText,
    placeholderDisabled = true,
    ...rest
  },
  ref
) {
  const opts = useMemo(() => {
    return pipe(
      options,
      o.fromPredicate((x): x is Option[] => Array.isArray(x)),
      o.map((opt) => generateOptions(opt)),
      o.alt(() =>
        pipe(
          groupedOptions,
          o.fromPredicate((x): x is GroupedOption[] => Array.isArray(x)),
          o.map((optGroup) => generateGroupedOptions(optGroup))
        )
      ),
      o.getOrElse(() => [<></>])
    );
  }, [groupedOptions, options]);

  return (
    <Box className={className}>
      <Box
        className={cx(
          styles.container({
            disabled: rest.disabled,
            error: hasError(error),
          })
        )}
      >
        <Label disabled={rest.disabled} floating rise htmlFor={name}>
          {!rest.required ? `${label}` : `${label} *`}
        </Label>
        <Box
          ref={ref}
          {...rest}
          className={styles.select({ placeholder: !rest.value })}
          as="select"
          name={name}
          aria-describedby={error || helpText ? `${name}-desc` : undefined}
          id={name}
        >
          {placeholder ? (
            <Box as="option" value="" disabled={placeholderDisabled}>
              {placeholder}
            </Box>
          ) : null}
          {opts}
        </Box>
      </Box>
      {error ? <InputError error={error} id={`${name}-desc`} /> : null}
      {helpText ? (
        <Typography intent="label" className="mt-2" id={`${name}-desc`}>
          {helpText}
        </Typography>
      ) : null}
    </Box>
  );
});
